import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ScanLine, X, Camera, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

/**
 * Barcode Scanner Component
 * - Opens camera and scans barcodes/QR codes
 * - Returns the scanned value via onScan callback
 * - Also supports physical USB scanners (they work as keyboard input - no special handling needed)
 */
export default function BarcodeScanner({ open, onClose, onScan, title = 'مسح الباركود' }) {
  const scannerRef = useRef(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [cameraId, setCameraId] = useState(null);
  const [cameras, setCameras] = useState([]);

  useEffect(() => {
    if (!open) return;

    const scannerId = 'qr-reader';
    let html5QrCode = null;

    const initScanner = async () => {
      setStarting(true);
      setError(null);
      try {
        // Get available cameras
        const devices = await Html5Qrcode.getCameras();
        setCameras(devices || []);
        
        if (!devices || devices.length === 0) {
          setError('لا توجد كاميرا متاحة');
          setStarting(false);
          return;
        }

        // Prefer back camera if available
        const backCamera = devices.find(d => 
          /back|rear|environment/i.test(d.label)
        );
        const selectedId = cameraId || backCamera?.id || devices[0].id;
        setCameraId(selectedId);

        // Wait for DOM element
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (!document.getElementById(scannerId)) {
          setError('فشل تحميل عنصر الكاميرا');
          setStarting(false);
          return;
        }

        html5QrCode = new Html5Qrcode(scannerId);
        scannerRef.current = html5QrCode;

        await html5QrCode.start(
          selectedId,
          {
            fps: 10,
            qrbox: { width: 250, height: 150 },
            aspectRatio: 1.5,
          },
          (decodedText) => {
            // Success callback
            setLastScan(decodedText);
            if (onScan) onScan(decodedText);
            // Auto-close after successful scan
            stopScanner();
            setTimeout(() => onClose && onClose(), 500);
          },
          () => {
            // Ignore decode errors
          }
        );
        setStarting(false);
      } catch (err) {
        console.error('Scanner error:', err);
        setError(err.message || 'فشل تشغيل الكاميرا. تأكد من السماح بالوصول للكاميرا.');
        setStarting(false);
      }
    };

    const stopScanner = async () => {
      try {
        if (scannerRef.current && scannerRef.current.isScanning) {
          await scannerRef.current.stop();
        }
        if (scannerRef.current) {
          await scannerRef.current.clear();
        }
      } catch (err) {
        // Ignore stop errors
      }
      scannerRef.current = null;
    };

    initScanner();

    return () => {
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cameraId]);

  const switchCamera = (newId) => {
    setCameraId(newId);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl text-white flex items-center gap-2">
            <ScanLine className="w-6 h-6 text-[#007aff]" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {error ? (
            <div className="p-4 bg-[#ff3b30]/10 border border-[#ff3b30]/20 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#ff3b30] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[#ff3b30] font-semibold mb-1">خطأ في الكاميرا</p>
                <p className="text-sm text-zinc-300">{error}</p>
                <p className="text-xs text-zinc-500 mt-2">
                  💡 تلميح: يمكنك استخدام قارئ باركود فيزيائي بدلاً من ذلك بكتابته في حقل البحث
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="relative bg-black rounded-lg overflow-hidden">
                <div id="qr-reader" className="w-full" data-testid="qr-reader-element"></div>
                {starting && (
                  <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/80 backdrop-blur-sm">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-[#007aff] mx-auto mb-2" />
                      <p className="text-zinc-300 text-sm">جاري تشغيل الكاميرا...</p>
                    </div>
                  </div>
                )}
              </div>

              {lastScan && (
                <div className="p-3 bg-[#34c759]/10 border border-[#34c759]/20 rounded-lg flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#34c759]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-400">تم المسح بنجاح:</p>
                    <p className="text-[#34c759] font-mono text-sm truncate">{lastScan}</p>
                  </div>
                </div>
              )}

              {cameras.length > 1 && (
                <div className="space-y-2">
                  <p className="text-xs text-zinc-400">تبديل الكاميرا:</p>
                  <div className="flex gap-2 flex-wrap">
                    {cameras.map((cam) => (
                      <Button
                        key={cam.id}
                        variant={cameraId === cam.id ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => switchCamera(cam.id)}
                        className={cameraId === cam.id ? 'bg-[#007aff]' : 'border-zinc-700'}
                      >
                        <Camera className="w-3 h-3 ml-1" />
                        {cam.label.slice(0, 20) || 'كاميرا'}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <p className="text-xs text-zinc-300">
                  💡 <strong>كيفية الاستخدام:</strong>
                </p>
                <ul className="text-xs text-zinc-400 mt-1 space-y-1 pr-4">
                  <li>• وجّه الكاميرا نحو الباركود (IMEI أو QR)</li>
                  <li>• سيتم القراءة تلقائياً عند التركيز</li>
                  <li>• تأكد من الإضاءة الجيدة</li>
                </ul>
              </div>
            </>
          )}

          <Button 
            variant="outline" 
            onClick={onClose} 
            className="w-full border-zinc-700"
            data-testid="close-scanner-button"
          >
            <X className="w-4 h-4 ml-2" />
            إغلاق
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
